export interface MPESettings {
  masterChannel: 1 | 16;
  memberChannelCount: number;
  pitchBendRange: number;
  pressureMode: 'channel-at' | 'poly-at' | 'cc';
  pressureCC: number;
  timbreCC: number;
  bendAutoReset: boolean;
}

export interface MPEVoice {
  noteId: string;
  channel: number;
  midiNote: number;
  velocity: number;
  pitchBend: number;
  pressure: number;
  timbre: number;
  state: 'active' | 'released';
}

export type MPEListener = (voices: MPEVoice[]) => void;

export const DEFAULT_MPE_SETTINGS: MPESettings = {
  masterChannel: 1,
  memberChannelCount: 15,
  pitchBendRange: 24,
  pressureMode: 'channel-at',
  pressureCC: 11,
  timbreCC: 74,
  bendAutoReset: true,
};
export class MPEService {
  private access: MIDIAccess | null = null;
  private output: MIDIOutput | null = null;
  private settings: MPESettings;
  private freeChannels: number[];
  private voiceByNoteId = new Map<string, MPEVoice>();
  private listeners = new Set<MPEListener>();
  private _enabled = false;

  constructor(settings?: Partial<MPESettings>) {
    this.settings = { ...DEFAULT_MPE_SETTINGS, ...settings };
    this.freeChannels = this.buildChannelPool();
  }

  async init(): Promise<void> {
    if (!('requestMIDIAccess' in navigator)) return;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
    } catch {
    }
  }

  getSettings(): Readonly<MPESettings> {
    return { ...this.settings };
  }

  updateSettings(patch: Partial<MPESettings>): void {
    this.settings = { ...this.settings, ...patch };
    if ('memberChannelCount' in patch || 'masterChannel' in patch) {
      this.resetChannelPool();
    }
    if (this.output) this.sendMCM();
  }

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    if (!enabled) this.panic();
  }

  isEnabled(): boolean {
    return this._enabled;
  }

  setOutput(output: MIDIOutput | null): void {
    this.output = output;
    if (output) this.sendMCM();
  }

  getAvailableOutputs(): MIDIOutput[] {
    if (!this.access) return [];
    const outputs: MIDIOutput[] = [];
    this.access.outputs.forEach((o) => outputs.push(o));
    return outputs;
  }

  getMidiAccess(): MIDIAccess | null {
    return this.access;
  }

  subscribe(listener: MPEListener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  activeVoices(): MPEVoice[] {
    return Array.from(this.voiceByNoteId.values()).filter(v => v.state === 'active');
  }

  noteOn(noteId: string, midiNote: number, velocity: number): void {
    if (!this._enabled || !this.output) return;
    const ch = this.allocate();
    if (ch === null) return;

    const chIdx = ch - 1;

    if (this.settings.bendAutoReset) {
      this.output.send([0xE0 | chIdx, 0x00, 0x40]);
    }
    this.output.send([0xB0 | chIdx, this.settings.timbreCC, 64]);
    this.sendPressureRaw(chIdx, midiNote, 0);
    this.output.send([0x90 | chIdx, midiNote, Math.round(velocity * 127)]);

    const voice: MPEVoice = {
      noteId,
      channel: ch,
      midiNote,
      velocity,
      pitchBend: 0,
      pressure: 0,
      timbre: 0.5,
      state: 'active',
    };
    this.voiceByNoteId.set(noteId, voice);
    this.notify();
  }

  noteOff(noteId: string, midiNote: number): void {
    if (!this._enabled || !this.output) return;
    const voice = this.voiceByNoteId.get(noteId);
    if (!voice) return;

    const chIdx = voice.channel - 1;
    this.output.send([0x80 | chIdx, midiNote, 64]);
    voice.state = 'released';
    this.notify();
    this.release(noteId);
  }

  sendPressure(noteId: string, pressure: number): void {
    if (!this._enabled || !this.output) return;
    const voice = this.voiceByNoteId.get(noteId);
    if (!voice) return;

    this.sendPressureRaw(voice.channel - 1, voice.midiNote, pressure);
    voice.pressure = pressure;
    this.notify();
  }

  sendSlide(noteId: string, value: number): void {
    if (!this._enabled || !this.output) return;
    const voice = this.voiceByNoteId.get(noteId);
    if (!voice) return;

    const chIdx = voice.channel - 1;
    this.output.send([0xB0 | chIdx, this.settings.timbreCC, Math.round(value * 127)]);
    voice.timbre = value;
    this.notify();
  }

  sendPitchBend(noteId: string, semitones: number): void {
    if (!this._enabled || !this.output) return;
    const voice = this.voiceByNoteId.get(noteId);
    if (!voice) return;

    const chIdx = voice.channel - 1;
    const normalized = semitones / this.settings.pitchBendRange;
    const uint14 = Math.round((normalized + 1) * 8191.5);
    const lsb = uint14 & 0x7F;
    const msb = (uint14 >> 7) & 0x7F;
    this.output.send([0xE0 | chIdx, lsb, msb]);
    voice.pitchBend = semitones;
    this.notify();
  }

  panic(): void {
    if (this.output) {
      const channels = this.buildChannelPool();
      for (const ch of channels) {
        this.output.send([0xB0 | (ch - 1), 123, 0]);
      }
    }
    this.voiceByNoteId.clear();
    this.freeChannels = this.buildChannelPool();
    this.notify();
  }

  dispose(): void {
    this.panic();
    this.listeners.clear();
    this.output = null;
    this.access = null;
  }

  private buildChannelPool(): number[] {
    const channels: number[] = [];
    if (this.settings.masterChannel === 1) {
      const end = Math.min(1 + this.settings.memberChannelCount, 16);
      for (let ch = 2; ch <= end; ch++) channels.push(ch);
    } else {
      const start = Math.max(16 - this.settings.memberChannelCount, 1);
      for (let ch = start; ch <= 15; ch++) channels.push(ch);
    }
    return channels;
  }

  private resetChannelPool(): void {
    this.panic();
  }

  private allocate(): number | null {
    if (this.freeChannels.length === 0) return null;
    const ch = this.freeChannels.shift();
    if (ch === undefined) return null;
    return ch;
  }

  private release(noteId: string): void {
    const voice = this.voiceByNoteId.get(noteId);
    if (!voice) return;
    this.freeChannels.push(voice.channel);
    this.voiceByNoteId.delete(noteId);
  }

  private notify(): void {
    const voices = Array.from(this.voiceByNoteId.values());
    for (const listener of this.listeners) {
      listener(voices);
    }
  }

  private sendPressureRaw(chIdx: number, midiNote: number, pressure: number): void {
    if (!this.output) return;
    const val = Math.round(pressure * 127);
    switch (this.settings.pressureMode) {
      case 'channel-at':
        this.output.send([0xD0 | chIdx, val]);
        break;
      case 'poly-at':
        this.output.send([0xA0 | chIdx, midiNote, val]);
        break;
      case 'cc':
        this.output.send([0xB0 | chIdx, this.settings.pressureCC, val]);
        break;
    }
  }

  private sendMCM(): void {
    if (!this.output) return;

    const managerIdx = this.settings.masterChannel - 1;
    const status = 0xB0 | managerIdx;

    this.output.send([status, 101, 0]);
    this.output.send([status, 100, 6]);
    this.output.send([status, 6, this.settings.memberChannelCount]);
    this.output.send([status, 101, 127]);
    this.output.send([status, 100, 127]);

    const otherZoneIdx = this.settings.masterChannel === 1 ? 15 : 0;
    const otherStatus = 0xB0 | otherZoneIdx;
    this.output.send([otherStatus, 101, 0]);
    this.output.send([otherStatus, 100, 6]);
    this.output.send([otherStatus, 6, 0]);
    this.output.send([otherStatus, 101, 127]);
    this.output.send([otherStatus, 100, 127]);

    this.sendPitchBendSensitivity(managerIdx, this.settings.pitchBendRange);

    const memberChannels = this.buildChannelPool();
    for (const ch of memberChannels) {
      this.sendPitchBendSensitivity(ch - 1, this.settings.pitchBendRange);
    }
  }

  private sendPitchBendSensitivity(chIdx: number, semitones: number): void {
    if (!this.output) return;
    const status = 0xB0 | chIdx;
    this.output.send([status, 101, 0]);
    this.output.send([status, 100, 0]);
    this.output.send([status, 6, semitones & 0x7F]);
    this.output.send([status, 38, 0]);
    this.output.send([status, 101, 127]);
    this.output.send([status, 100, 127]);
  }
}
